"use client"

import { useState } from "react"
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material"
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts"
import { useAuth } from "../../contexts/AuthContext"

const AnalyticsDashboard = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false) // Set to false initially
  const [error, setError] = useState(null)
  const [tabValue, setTabValue] = useState(0)
  const [timeframe, setTimeframe] = useState("last_year")

  // Mock data - using this directly instead of trying to fetch
  const dashboardStats = {
    totalCustomers: 1250,
    activeSubscriptions: 980,
    mrr: 45600,
    arpu: 46.53,
    churnRate: 3.2,
  }

  const statusDistribution = [
    { name: "Active", value: 980 },
    { name: "Canceled", value: 120 },
    { name: "Past Due", value: 45 },
    { name: "Trialing", value: 105 },
  ]

  const revenueByPlan = [
    { name: "Basic", value: 12500 },
    { name: "Pro", value: 18700 },
    { name: "Enterprise", value: 14400 },
  ]

  const subscriptionGrowth = [
    { month: "Jan", subscriptions: 820 },
    { month: "Feb", subscriptions: 850 },
    { month: "Mar", subscriptions: 880 },
    { month: "Apr", subscriptions: 890 },
    { month: "May", subscriptions: 910 },
    { month: "Jun", subscriptions: 940 },
    { month: "Jul", subscriptions: 950 },
    { month: "Aug", subscriptions: 970 },
    { month: "Sep", subscriptions: 980 },
    { month: "Oct", subscriptions: 1010 },
    { month: "Nov", subscriptions: 1040 },
    { month: "Dec", subscriptions: 1080 },
  ]

  const revenueOverTime = [
    { month: "Jan", revenue: 38000 },
    { month: "Feb", revenue: 39500 },
    { month: "Mar", revenue: 41000 },
    { month: "Apr", revenue: 41500 },
    { month: "May", revenue: 42800 },
    { month: "Jun", revenue: 43900 },
    { month: "Jul", revenue: 44200 },
    { month: "Aug", revenue: 45000 },
    { month: "Sep", revenue: 45600 },
    { month: "Oct", revenue: 47000 },
    { month: "Nov", revenue: 48500 },
    { month: "Dec", revenue: 50200 },
  ]

  const mrrMovement = {
    newBusiness: 3200,
    expansion: 1800,
    contraction: -800,
    churn: -1200,
    reactivation: 600,
    netMrr: 3600,
  }

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d"]

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const handleTimeframeChange = (event) => {
    setTimeframe(event.target.value)
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(value)
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Subscription Analytics
      </Typography>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Customers
              </Typography>
              <Typography variant="h5">{dashboardStats.totalCustomers.toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Subscriptions
              </Typography>
              <Typography variant="h5">{dashboardStats.activeSubscriptions.toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Monthly Recurring Revenue
              </Typography>
              <Typography variant="h5">{formatCurrency(dashboardStats.mrr)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg. Revenue Per User
              </Typography>
              <Typography variant="h5">{formatCurrency(dashboardStats.arpu)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Churn Rate
              </Typography>
              <Typography variant="h5">{dashboardStats.churnRate}%</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs for different views */}
      <Paper sx={{ mb: 4 }}>
        <Tabs value={tabValue} onChange={handleTabChange} centered>
          <Tab label="Overview" />
          <Tab label="Revenue" />
          <Tab label="Subscriptions" />
        </Tabs>
      </Paper>

      {/* Overview Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Subscription Status Distribution
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusDistribution}
                        cx="50%"
                        cy="50%"
                        labelLine={true}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {statusDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => value.toLocaleString()} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Revenue by Plan
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={revenueByPlan} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={(value) => `$${value / 1000}k`} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="value" fill="#8884d8">
                        {revenueByPlan.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Revenue Tab */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6">Revenue Over Time</Typography>
                  <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel>Timeframe</InputLabel>
                    <Select value={timeframe} label="Timeframe" onChange={handleTimeframeChange}>
                      <MenuItem value="last_month">Last Month</MenuItem>
                      <MenuItem value="last_quarter">Last Quarter</MenuItem>
                      <MenuItem value="last_year">Last Year</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={revenueOverTime} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(value) => `$${value / 1000}k`} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Line type="monotone" dataKey="revenue" stroke="#8884d8" activeDot={{ r: 8 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  MRR Movement
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={4} md={2}>
                    <Paper sx={{ p: 2, bgcolor: "#e3f2fd", textAlign: "center" }}>
                      <Typography variant="subtitle2">New Business</Typography>
                      <Typography variant="h6" color="primary">
                        +{formatCurrency(mrrMovement.newBusiness)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <Paper sx={{ p: 2, bgcolor: "#e8f5e9", textAlign: "center" }}>
                      <Typography variant="subtitle2">Expansion</Typography>
                      <Typography variant="h6" color="success.main">
                        +{formatCurrency(mrrMovement.expansion)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <Paper sx={{ p: 2, bgcolor: "#fff3e0", textAlign: "center" }}>
                      <Typography variant="subtitle2">Contraction</Typography>
                      <Typography variant="h6" color="warning.main">
                        {formatCurrency(mrrMovement.contraction)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <Paper sx={{ p: 2, bgcolor: "#ffebee", textAlign: "center" }}>
                      <Typography variant="subtitle2">Churn</Typography>
                      <Typography variant="h6" color="error.main">
                        {formatCurrency(mrrMovement.churn)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <Paper sx={{ p: 2, bgcolor: "#f3e5f5", textAlign: "center" }}>
                      <Typography variant="subtitle2">Reactivation</Typography>
                      <Typography variant="h6" color="secondary.main">
                        +{formatCurrency(mrrMovement.reactivation)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <Paper sx={{ p: 2, bgcolor: "#e8eaf6", textAlign: "center" }}>
                      <Typography variant="subtitle2">Net MRR</Typography>
                      <Typography variant="h6" color={mrrMovement.netMrr >= 0 ? "success.main" : "error.main"}>
                        {mrrMovement.netMrr >= 0 ? "+" : ""}
                        {formatCurrency(mrrMovement.netMrr)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Subscriptions Tab */}
      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Subscription Growth
                </Typography>
                <Box sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={subscriptionGrowth} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip formatter={(value) => value.toLocaleString()} />
                      <Legend />
                      <Line type="monotone" dataKey="subscriptions" stroke="#82ca9d" activeDot={{ r: 8 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  )
}

export default AnalyticsDashboard

